import React from "react";

import { faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard, Pagination, Text } from "@performyard/py-components";
import type { ExtendedWorkspace } from "@performyard/service-contracts";

import FilterChips from "../../../../components/FilterChips";
import ContentModal from "../../../../components/Modal/Templates/ContentModal";
import PageHeading from "../../../../components/PageHeading";
import Spacer from "../../../../components/Spacer";
import CreateWorkspaceModal from "./CreateWorkspaceForm";
import UpdateWorkspaceModal from "./UpdateWorkspaceForm";
import WorkspacesFilterPanel from "./WorkspacesFilterPanel";
import WorkspacesTable from "./WorkspacesTable";

import { type WorkspacesFilters } from "../schemas";

import { useCurrentUser, useMeetingWorkspacesSearch, useUpdateMeetingWorkspace } from "../../../../api/hooks";
import { useIsNewNavigation } from "../../../../api/hooks/defs/navigation";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { convertWorkspaceFiltersToFilterChips, convertWorkspaceFiltersToSearchParams } from "../utils";

import InfoPageTemplate from "../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../../../Error";
import { workspacesTableConfig } from "../config";
import css from "./Workspaces.module.scss";

const PAGE_TITLE = "My Meeting Workspaces";

const Workspaces = () => {
	const isNewNav = useIsNewNavigation();
	const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false);
	const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
	const { value: ffMeetings, query: ffMeetingsQuery } = useFeatureFlagQuery("ff-is-meetings-enabled", false);
	// Create workspace modal state
	const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] = React.useState(false);
	// Confirm archive workspace modal state
	const [isConfirmArchiveWorkspaceModalOpen, setIsConfirmArchiveWorkspaceModalOpen] = React.useState<string | null>(
		null
	);
	// Update workspace modal state
	const [editingWorkspace, setEditingWorkspace] = React.useState<ExtendedWorkspace | null>(null);
	// Query state for the page (filters, sort, search, page)
	const {
		filters,
		setFilters,
		sort,
		setSort,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
		pageNumber,
		setPageNumber,
	} = useInfoPageTemplateQueryState<WorkspacesFilters>({
		defaultSortName: workspacesTableConfig.defaultSortName,
		defaultSortDirection: workspacesTableConfig.defaultSortDirection,
		defaultPageNumber: workspacesTableConfig.defaultPageNumber,
		fallbackFilters: workspacesTableConfig.fallbackFilters,
	});
	// Make sure status is always included (in case we recieve a bad URL)
	if (filters.status === undefined) {
		setFilters({ ...filters, status: "active" });
	}

	// Current user will be included in search params
	const userId = currentUser?.me?.authorization?.payload.user_id || "";
	const {
		data: workspaces,
		totalCount: totalWorkspacesCount,
		isLoading: isFetchingWorkspaces,
	} = useMeetingWorkspacesSearch(
		convertWorkspaceFiltersToSearchParams(filters, debouncedSearchValue, sort, pageNumber, userId)
	);

	// Update workspace request for status changes
	const { mutate: updateWorkspace, isPending: isUpdatingWorkspace } = useUpdateMeetingWorkspace({
		onSuccess: () => {
			setIsConfirmArchiveWorkspaceModalOpen(null);
		},
	});

	// Wait for feature flag to load, then show access denied if disabled
	if (ffMeetingsQuery.isLoading) {
		return null;
	}

	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	/** Header */
	const actionButtons = [
		<ButtonStandard
			key="createWorkspaceButton"
			data-testid="createWorkspaceButton"
			color="green"
			iconLeft={faPlus}
			onClick={() => setIsCreateWorkspaceModalOpen(true)}
		>
			Create Meeting Workspace
		</ButtonStandard>,
	];

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			searchPlaceholder="Search meeting workspaces..."
			onFilterButtonPress={() => setIsFilterPanelOpen(true)}
		/>
	);

	const toolbarInformationRow = (
		<FilterChips
			filters={convertWorkspaceFiltersToFilterChips(filters)}
			label="Filters applied: "
			onDeleteFilter={(id) => {
				const currentFilters = { ...filters };
				// Default to active status if status is deleted
				if (id === "status") {
					currentFilters.status = "active";
				} else if (id === "upcomingMeeting") {
					delete currentFilters.upcomingMeetingStart;
					delete currentFilters.upcomingMeetingEnd;
				} else if (id === "previousMeeting") {
					delete currentFilters.previousMeetingStart;
					delete currentFilters.previousMeetingEnd;
				} else {
					delete currentFilters[id];
				}
				setFilters({ ...currentFilters });
			}}
			nonDismissibleFilters={filters.status === "active" ? ["status"] : []} // Active is not dismissible
		/>
	);

	/** Filter panel */
	const filterPanel = (
		<WorkspacesFilterPanel
			isOpen={isFilterPanelOpen}
			onClose={() => setIsFilterPanelOpen(false)}
			onApplyFilters={(filters) => {
				setFilters(filters);
			}}
			filters={filters}
		/>
	);

	/** Table */
	const mainContent = (
		<div>
			{workspaces && (
				<>
					<WorkspacesTable
						workspaces={workspaces}
						isFetchingWorkspaces={isLoadingUser || isFetchingWorkspaces || isUpdatingWorkspace}
						activeSort={sort.name}
						activeSortDirection={sort.direction}
						handleColumnHeaderClick={(sortValue) => setSort(sortValue)}
						userId={userId}
						handleEditWorkspace={(workspace) => setEditingWorkspace(workspace)}
						handleWorkspaceStatusChange={(workspaceId, status) => {
							if (status === "archived") {
								setIsConfirmArchiveWorkspaceModalOpen(workspaceId);
							} else {
								updateWorkspace({
									workspaceId,
									data: { status },
								});
							}
						}}
					/>
					<Spacer height={3} />
					<div data-testid="paginationContainer" className="mx-auto flex w-full max-w-[400px] justify-center">
						<Pagination
							currentPage={pageNumber}
							pageCount={Math.ceil(totalWorkspacesCount / workspacesTableConfig.pageSize)}
							onPageChange={(newPage: number) => setPageNumber(newPage)}
						/>
					</div>
					<CreateWorkspaceModal
						isOpen={isCreateWorkspaceModalOpen}
						handleClose={() => setIsCreateWorkspaceModalOpen(false)}
					/>
					<UpdateWorkspaceModal
						isOpen={editingWorkspace !== null}
						handleClose={() => setEditingWorkspace(null)}
						workspace={editingWorkspace}
					/>
					<ContentModal
						isOpen={isConfirmArchiveWorkspaceModalOpen !== null}
						handleClose={() => setIsConfirmArchiveWorkspaceModalOpen(null)}
						size="large"
						contentClassName={css.archiveWorkspaceModalContent}
						buttons={[
							<div key="cancel">
								<ButtonStandard
									size="large"
									onClick={() => setIsConfirmArchiveWorkspaceModalOpen(null)}
								>
									Cancel
								</ButtonStandard>
							</div>,
							<div key="save">
								<ButtonStandard
									size="large"
									disabled={isUpdatingWorkspace}
									color="green"
									onClick={() => {
										if (isConfirmArchiveWorkspaceModalOpen) {
											updateWorkspace({
												workspaceId: isConfirmArchiveWorkspaceModalOpen,
												data: { status: "archived" },
											});
										}
									}}
								>
									Yes, Archive Workspace
								</ButtonStandard>
							</div>,
						]}
					>
						<Text size="sub-heading" weight="semi-bold">
							Archive Workspace?
						</Text>
						<Spacer height={2} />
						<Text size="paragraph-1" weight="semi-bold">
							Are you sure you want to archive this workspace?
						</Text>
					</ContentModal>
				</>
			)}
		</div>
	);

	return (
		<>
			<InfoPageTemplate
				employeeID={userId}
				pageTitle={PAGE_TITLE}
				pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
				mainContent={mainContent}
				actionButtons={actionButtons}
				toolbarActionRow={toolbarActionRow}
				toolbarInformationRow={toolbarInformationRow}
				navigationLinks={[
					{ key: "my-workspaces", label: "Workspaces I'm In", href: "/meeting-workspaces" },
					{ key: "action-items", label: "Action Items", href: "/meeting-workspaces?tab=action-items" },
				]}
				activeNavigationLinkKey="my-workspaces"
				drawer={filterPanel}
			/>
			<Spacer height={5} />
		</>
	);
};

export default Workspaces;
